import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { ChevronDown, X } from "../../icons/svg/outline";
import useOutsideClick from "../../../hooks/useOutsideClick";
import { itemMatchesSearchQuery } from "../../../utils/common/searchFilter";

/**
 * Normal-looking single-select dropdown with type-to-search behavior.
 *
 * @param {Object} props - Component props.
 * @param {Array<{ value?: string|number, id?: string|number, label: string }>} props.options - Options.
 * @param {string|number|null} props.value - Selected option value.
 * @param {(value: string|null) => void} props.onChange - Change handler.
 * @param {string} [props.label] - Optional visible field label above the control.
 * @param {string} [props.placeholder="Select option"] - Empty placeholder.
 * @param {boolean} [props.disabled=false] - Whether the control is disabled.
 * @param {boolean} [props.clearable=true] - Whether the selected value can be cleared.
 * @param {boolean} [props.searchWhenOverflow=false] - When true, enable type-to-search only if the
 *   options list overflows (scrollbar). When false, search is always available.
 * @param {string} [props.className=""] - Wrapper classes.
 * @param {string} [props.inputClassName=""] - Extra input classes.
 * @param {string} [props.listboxClassName="z-[9999]"] - Extra portaled listbox classes.
 * @param {string} [props.ariaLabel] - Accessible name.
 * @returns {JSX.Element}
 */
const SearchableDropdown = ({
  options = [],
  value,
  onChange,
  label,
  placeholder = "Select option",
  disabled = false,
  clearable = true,
  searchWhenOverflow = false,
  className = "",
  inputClassName = "",
  listboxClassName = "z-[9999]",
  ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [keyboardIndex, setKeyboardIndex] = useState(0);
  const [listOverflows, setListOverflows] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const listboxId = useId();
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const normalizedOptions = useMemo(
    () =>
      options.map((option) => ({
        ...option,
        value: String(option.value ?? option.id ?? ""),
      })),
    [options]
  );

  const selectedOption = useMemo(
    () =>
      normalizedOptions.find(
        (option) => option.value === String(value ?? "")
      ),
    [normalizedOptions, value]
  );

  // Search stays on whenever overflow mode is off; otherwise only with a scrollbar.
  const searchEnabled = !searchWhenOverflow || listOverflows;

  const filteredOptions = useMemo(() => {
    if (!searchEnabled || !searchTerm) {
      return normalizedOptions;
    }
    return normalizedOptions.filter((option) =>
      itemMatchesSearchQuery(option, searchTerm, ["label"])
    );
  }, [normalizedOptions, searchEnabled, searchTerm]);

  const closeList = useCallback(() => {
    setIsOpen(false);
    setSearchTerm("");
    setKeyboardIndex(0);
    setListOverflows(false);
  }, []);

  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) {
      return;
    }
    const rect = containerRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  const openList = useCallback(() => {
    if (disabled) {
      return;
    }
    setIsOpen(true);
    setKeyboardIndex(0);
    updateDropdownPosition();
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }, [disabled, updateDropdownPosition]);

  useOutsideClick(containerRef, closeList, {
    ignoreSelector: "[data-searchable-dropdown-listbox]",
  });

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    updateDropdownPosition();
    window.addEventListener("scroll", updateDropdownPosition, true);
    window.addEventListener("resize", updateDropdownPosition);
    return () => {
      window.removeEventListener("scroll", updateDropdownPosition, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [isOpen, updateDropdownPosition]);

  // Measure list overflow after the portaled listbox paints (max-h-60 + scroll).
  useLayoutEffect(() => {
    if (!isOpen || !searchWhenOverflow) {
      return;
    }
    const el = listRef.current;
    if (!el) {
      return;
    }
    setListOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [isOpen, searchWhenOverflow, normalizedOptions]);

  useEffect(() => {
    setKeyboardIndex(0);
  }, [searchTerm]);

  useEffect(() => {
    if (!isOpen || !listRef.current) {
      return;
    }
    listRef.current.children[keyboardIndex]?.scrollIntoView({
      block: "nearest",
    });
  }, [keyboardIndex, isOpen]);

  /**
   * Commits an option value and closes the list.
   *
   * @param {{value: *}} option Normalized option.
   * @returns {void}
   */
  const selectOption = (option) => {
    onChange?.(option.value);
    closeList();
    inputRef.current?.blur();
  };

  /**
   * Clears the selection without bubbling to the open handler.
   *
   * @param {React.MouseEvent} event Clear-button click.
   * @returns {void}
   */
  const clearSelection = (event) => {
    event.stopPropagation();
    onChange?.(null);
    closeList();
  };

  /**
   * Keyboard navigation: arrows open/move, Enter selects, Escape closes.
   *
   * @param {React.KeyboardEvent} event Input keydown event.
   * @returns {void}
   */
  const handleKeyDown = (event) => {
    if (!isOpen && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      event.preventDefault();
      openList();
      return;
    }

    if (!isOpen) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setKeyboardIndex((index) =>
        Math.min(index + 1, Math.max(filteredOptions.length - 1, 0))
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setKeyboardIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const option = filteredOptions[keyboardIndex];
      if (option) {
        selectOption(option);
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      closeList();
      inputRef.current?.blur();
    }
  };

  const inputValue =
    searchEnabled && isOpen ? searchTerm : selectedOption?.label || "";
  const inputPlaceholder =
    searchEnabled && isOpen ? "Search..." : placeholder;
  const controlAriaLabel =
    ariaLabel ?? label ?? placeholder ?? "Searchable dropdown";

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label ? (
        <span className="text-xs font-medium text-gray-600">{label}</span>
      ) : null}

      <div className="relative" ref={containerRef}>
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-label={controlAriaLabel}
          aria-expanded={isOpen}
          aria-controls={isOpen ? listboxId : undefined}
          aria-autocomplete={searchEnabled ? "list" : "none"}
          value={inputValue}
          placeholder={inputPlaceholder}
          disabled={disabled}
          readOnly={!searchEnabled}
          onFocus={openList}
          onClick={openList}
          onChange={(event) => {
            if (!searchEnabled) {
              return;
            }
            setSearchTerm(event.target.value);
            if (!isOpen) {
              setIsOpen(true);
            }
          }}
          onKeyDown={handleKeyDown}
          className={`w-full cursor-pointer rounded-lg border border-gray-300 bg-white px-3 py-2 pr-10 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-secondary-400 focus:ring-4 focus:ring-secondary-100 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-60 ${
            !searchEnabled ? "caret-transparent" : ""
          } ${inputClassName}`}
        />

        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400">
          <ChevronDown
            className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </div>

        {clearable && value && !disabled ? (
          <button
            type="button"
            onClick={clearSelection}
            className="absolute right-8 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Clear selection"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}

        {isOpen && !disabled
          ? createPortal(
              <ul
                id={listboxId}
                ref={listRef}
                role="listbox"
                aria-label={`${controlAriaLabel} options`}
                data-searchable-dropdown-listbox={listboxId}
                className={`fixed max-h-60 overflow-y-auto rounded-lg border border-gray-300 bg-white py-1 font-inter shadow-lg ${listboxClassName}`}
                style={{
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  width: `${dropdownPosition.width}px`,
                }}
              >
                {filteredOptions.length === 0 ? (
                  <li className="px-3 py-2 text-sm text-gray-500">
                    No options found
                  </li>
                ) : (
                  filteredOptions.map((option, index) => (
                    <li
                      key={option.value}
                      role="option"
                      aria-selected={option.value === String(value ?? "")}
                    >
                      <button
                        type="button"
                        className={`w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 ${
                          index === keyboardIndex ? "bg-gray-100" : ""
                        } ${
                          option.value === String(value ?? "")
                            ? "font-semibold text-primary"
                            : ""
                        }`}
                        onMouseDown={(event) => event.preventDefault()}
                        onMouseEnter={() => setKeyboardIndex(index)}
                        onClick={() => selectOption(option)}
                      >
                        {option.label}
                      </button>
                    </li>
                  ))
                )}
              </ul>,
              document.body
            )
          : null}
      </div>
    </div>
  );
};

export default SearchableDropdown;
